// src/pages/ResultsGatePage.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import Footer from '../components/common/Footer';
import { Input } from '../components/common/Input';
import { useLang } from '../context/LangContext';
import { checkCompareCode } from '../api/results';
import { fadeIn } from '../utils/animations';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Center = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Box = styled(Card)`
  width: 100%;
  max-width: 480px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const Title = styled.h1`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const InfoText = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Field = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textLight};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
  }
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Actions = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

/* Код результата: 6–10 латинских букв или цифр (та же проверка, что и на backend). */
const normalizeCode = (value: string) => value.trim().toUpperCase();
const isValidCode = (code: string) => /^[A-Z0-9]{6,15}$/.test(code);

/* ── Component ── */

const ResultsGatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [resultCode, setResultCode] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = normalizeCode(resultCode);
    if (!code) {
      setError(t('resultCodeRequired'));
      return;
    }
    if (!isValidCode(code)) {
      setError(t('resultCodeInvalid'));
      return;
    }
    setError('');
    setChecking(true);
    try {
      const check = await checkCompareCode(code);
      navigate(check.code_type === 'comparison'
        ? `/compare-result/${encodeURIComponent(code)}`
        : `/result/${encodeURIComponent(code)}`);
    } catch {
      // Не удалось определить тип — ведём на стандартную страницу результата.
      navigate(`/result/${encodeURIComponent(code)}`);
    } finally {
      setChecking(false);
    }
  };

  return (
    <PageContainer>
      <Center>
        <Box>
          <Title>{t('viewResultsLink')}</Title>
          <InfoText>{t('resultCodePrompt')}</InfoText>
          <form onSubmit={handleSubmit}>
            <Field>
              <label htmlFor="result-code">{t('resultCodeLabel')}</label>
              <Input
                id="result-code"
                value={resultCode}
                onChange={(e) => { setResultCode(e.target.value.toUpperCase()); setError(''); }}
                placeholder={t('resultCodePlaceholder')}
                maxLength={15}
                fullWidth
                autoFocus
              />
            </Field>
            {error && <ErrorText>{error}</ErrorText>}
            <Actions>
              <Button variant="outline" size="lg" type="submit" fullWidth disabled={checking}>
                {checking ? t('genLoading') : t('findConclusion')}
              </Button>
              <div style={{ marginTop: 42.5 }}>
                <Button variant="outline" size="lg" fullWidth onClick={() => navigate('/compare-entry')}>
                  {t('compareProfiles')}
                </Button>
              </div>
            </Actions>
          </form>
        </Box>
      </Center>
      <Footer />
    </PageContainer>
  );
};

export default ResultsGatePage;